import { state } from '@angular/animations';
import { SelectionModel } from '@angular/cdk/collections';
import { AfterViewInit, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { concatMap, forkJoin, map, of } from 'rxjs';
import { CarrierWithPayerAndState } from 'src/app/Models/CarrierWithPayerAndState';
import { PayerEntity } from 'src/app/Models/PayerEntity';
import { PayerMappingChange } from 'src/app/Models/PayerMappingChange';
import { StateSelection } from 'src/app/Models/StateSelection';
import { DataService } from 'src/app/shared/data.service';

@Component({
    selector: 'app-edit-details',
    templateUrl: './edit-details.component.html',
    styleUrls: ['./edit-details.component.css'],
    standalone: false
})
export class EditDetailsComponent implements AfterViewInit {
  errorMessage = '';
  @Input() carriers!: CarrierWithPayerAndState[];
  @Input() selectedRows!: CarrierWithPayerAndState[];
  @Input() isAddButtonClicked!: boolean;
  @Output() closeEditDetails = new EventEmitter();
  @Output() refreshCarrierTable = new EventEmitter();
  @Output() resetAddButton = new EventEmitter();

  displayedColumnsStates: string[] = ['selectedPayers', 'allStatesSelect', 'caStateSelect', 'ilStateSelect', 'azStateSelect', 'gaStateSelect'];
  listOfPayers: MatTableDataSource<PayerEntity> = new MatTableDataSource<PayerEntity>([]);
  displayedColumns: string[] = ['select', 'payerName', 'payerMnemonic', 'payerInsuranceType'];
  payerSelection = new SelectionModel<PayerEntity>(false, []);
  totalCount: number = 0;
  pageSize: number = 500;
  statesSelected: StateSelection[] = [];
  isSaveEnabled: boolean = false;
  filter: string = '';

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(private dataSvc: DataService) { }

  ngOnInit(): void {
    this.getAllPayers();
  }

  ngAfterViewInit() {
    this.listOfPayers.paginator = this.paginator;
    this.listOfPayers.sort = this.sort;
  }

  ngOnChanges() {
    this.statesSelected = this.selectedRows.map((row: CarrierWithPayerAndState) => {
      return {
        ...row, 
        selections: {
          national: row.payerState === null && row.payerName !== null,
          ca: row.payerState === 'CA',
          il: row.payerState === 'IL',
          az: row.payerState === 'AZ',
          ga: row.payerState === 'GA',
        }
      };
    });
    
  }

  isAllStatesCheckboxDisabled(row: CarrierWithPayerAndState) {
    if (this.isAddButtonClicked) return true;
    return false;
  }

  isCaStateCheckboxDisabled(row: CarrierWithPayerAndState) {
    if (this.isAddButtonClicked && row.payerState === "CA") return true;
    const rowsWithSameCarrierName = this.carriers.filter((r: CarrierWithPayerAndState) => r.carrierName === row.carrierName);
    const hasCaPayerState = rowsWithSameCarrierName.some((r: CarrierWithPayerAndState) => r.payerState === "CA");
    if (hasCaPayerState) return true;
    return false;
  }


  isIlStateCheckboxDisabled(row: CarrierWithPayerAndState) {
    if (this.isAddButtonClicked && row.payerState === "IL") return true;
    const rowsWithSameCarrierName = this.carriers.filter((r: CarrierWithPayerAndState) => r.carrierName === row.carrierName);
    const hasIlPayerState = rowsWithSameCarrierName.some((r: CarrierWithPayerAndState) => r.payerState === "IL");
    if (hasIlPayerState) return true;
    return false;
  }

  isAzStateCheckboxDisabled(row: CarrierWithPayerAndState) {
    if (this.isAddButtonClicked && row.payerState === "AZ") return true;
    const rowsWithSameCarrierName = this.carriers.filter((r: CarrierWithPayerAndState) => r.carrierName === row.carrierName);
    const hasAzPayerState = rowsWithSameCarrierName.some((r: CarrierWithPayerAndState) => r.payerState === "AZ");
    if (hasAzPayerState) return true;
    return false;
  }

  isGaStateCheckboxDisabled(row: CarrierWithPayerAndState) {
    if (this.isAddButtonClicked && row.payerState === "GA") return true;
    const rowsWithSameCarrierName = this.carriers.filter((r: CarrierWithPayerAndState) => r.carrierName === row.carrierName);
    const hasGaPayerState = rowsWithSameCarrierName.some((r: CarrierWithPayerAndState) => r.payerState === "GA");
    if (hasGaPayerState) return true;
    return false;
  }

  toggleState(row: any, state: string) {
    row.selections[state] = !row.selections[state];
    if (row.selections[state]) {
      if (state === 'national') {
        row.selections['ca'] = false;
        row.selections['il'] = false;
        row.selections['az'] = false;
        row.selections['ga'] = false;
      }else if(state === 'il') {
        row.selections['national'] = false;
        row.selections['ca'] = false;
        row.selections['az'] = false;
        row.selections['ga'] = false;
      }else if(state === 'az') {
        row.selections['national'] = false;
        row.selections['ca'] = false;
        row.selections['il'] = false;
        row.selections['ga'] = false;
      }else if(state === 'ga') {
        row.selections['national'] = false;
        row.selections['ca'] = false;
        row.selections['il'] = false;
        row.selections['az'] = false;
      }else if(state === 'ca') {
        row.selections['national'] = false;
        row.selections['il'] = false;
        row.selections['az'] = false;
        row.selections['ga'] = false;
      }
    }
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.listOfPayers.filter = filterValue.trim().toLowerCase();

    if (this.listOfPayers.paginator) {
      this.listOfPayers.paginator.firstPage();
    }
  }

  clearFilter() {
    this.listOfPayers.filter = '';
    this.filter = '';
  }

  calculateChanges(){
    const changes: PayerMappingChange[] = [];
    const payerSelected = this.payerSelection.selected;
    const hasSelectedPayer = payerSelected.length > 0;
    this.statesSelected.forEach((row: StateSelection) => {
      if (hasSelectedPayer ){
        if (row.selections.national && row.payerId !== payerSelected[0].id) {
          changes.push({
            from: {
              carrierId: row.carrierId,
              payerId: row.payerId,
              payerState: row.payerState
            },
            to: {
              carrierId: row.carrierId,
              payerId: payerSelected[0].id,
              payerState: 'ALL'
            }
            
          });
        }
        if (row.selections.ca && (row.payerId !== payerSelected[0].id || row.payerState !== 'CA')) {
          changes.push({
            from: {
              carrierId: row.carrierId,
              payerId: this.isAddButtonClicked ? 0 : row.payerId,
              payerState: row.payerState
            },
            to: {
              carrierId: row.carrierId,
              payerId: payerSelected[0].id,
              payerState: 'CA'
            }
          });
        }
        if (row.selections.il && (row.payerId !== payerSelected[0].id || row.payerState !== 'IL')) {
          changes.push({
            from: {
              carrierId: row.carrierId,
              payerId: this.isAddButtonClicked ? 0 : row.payerId,
              payerState: row.payerState
            },
            to: {
              carrierId: row.carrierId,
              payerId: payerSelected[0].id,
              payerState: 'IL'
            }
          });
        }
        if (row.selections.az && (row.payerId !== payerSelected[0].id || row.payerState !== 'AZ')) {
          changes.push({
            from: {
              carrierId: row.carrierId,
              payerId: this.isAddButtonClicked ? 0 : row.payerId,
              payerState: row.payerState
            },
            to: {
              carrierId: row.carrierId,
              payerId: payerSelected[0].id,
              payerState: 'AZ'
            }
          });
        }
        if (row.selections.ga && (row.payerId !== payerSelected[0].id || row.payerState !== 'GA')) {
          changes.push({
            from: {
              carrierId: row.carrierId,
              payerId: this.isAddButtonClicked ? 0 : row.payerId,
              payerState: row.payerState
            },
            to: {
              carrierId: row.carrierId,
              payerId: payerSelected[0].id,
              payerState: 'GA'
            }
          });
        }
      }
    });
    const uniqueChanges = this.removeDuplicates(changes);
    this.isSaveEnabled = uniqueChanges.length > 0;
    return uniqueChanges;
  }

  removeDuplicates<T>(array: T[]){
   return array.filter((o: any, index: any, arr: any[]) =>
      arr.findIndex(item => JSON.stringify(item) === JSON.stringify(o)) === index);
  }

  saveButtonClicked() {
    const changes = this.calculateChanges();
    const allObservables = [
      ...changes.map(change => this.dataSvc.savePayerMappingChange(change))
    ];
    forkJoin(allObservables).subscribe(() => {
      this.resetAddButton.emit();
      this.payerSelection.clear();
      this.clearFilter();
      this.selectedRows = [];
      this.carriers = [];
      this.closeEditDetails.emit();
      this.refreshCarrierTable.emit();
    });
  }

  cancelButtonClicked() {
    this.isSaveEnabled = false;
    this.resetAddButton.emit();
    this.clearFilter();
    this.payerSelection.clear();
    this.ngOnChanges();
    this.closeEditDetails.emit();
  }

  hasResults(source: any) {
    return source.hasOwnProperty("results");
  }

  getAllPayers() {
    this.listOfPayers = new MatTableDataSource<PayerEntity>(); // Reset carriers array
    this.errorMessage = ''; // Reset error message

    // Get first page to determine total count
    this.dataSvc.getPayers(1, this.pageSize)
      .pipe(
        concatMap((response: any) => {
          if (this.hasResults(response)) {
            this.listOfPayers.data = [...response.results];
            this.totalCount = response.totalCount;

            // Calculate remaining pages
            const totalPages = Math.ceil(this.totalCount / this.pageSize);
            const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);

            // If there are more pages, request them in parallel
            if (remainingPages.length > 0) {
              return forkJoin(
                remainingPages.map(page => this.dataSvc.getPayers(page, this.pageSize))
              ).pipe(
                map(responses => {
                  responses.forEach((res: any) => {
                    if (this.hasResults(res)) {
                      this.listOfPayers.data = [...this.listOfPayers.data, ...res.results];
                    }
                    this.listOfPayers._updatePaginator(this.totalCount);
                  });
                })
              );
            }
          }
          return of(null);
        })
      )
      .subscribe();
  }
}
